import { Outlet, Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, FlaskConical, LayoutDashboard, GitBranch, Bell, MessageSquare, Activity as ActivityIcon, ClipboardSignature, Lightbulb, UserSquare2, Users, Tag } from "lucide-react";
import HeaderSearch from "@/components/HeaderSearch";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { VersionFooter } from "@/components/VersionFooter";
import { api } from "@/lib/api";

function AdminUpdatesBell() {
  const [state, setState] = useState({ unread_count: 0, items: [] });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  async function load() {
    try {
      const { data } = await api.get("/partner/updates");
      setState(data);
    } catch (_) { /* silent */ }
  }
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, []);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!e.target.closest("[data-testid='admin-updates-bell-wrap']")) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  async function markAllRead() {
    setBusy(true);
    try { await api.post("/partner/updates/mark-read"); await load(); } finally { setBusy(false); }
  }

  function summarise(it) {
    if (it.action === "note_added") return it.body || "(empty note)";
    if (it.action === "field_updated") return `updated ${it.field}: "${it.new || ""}"`;
    if (it.action === "stage_changed") return `moved to ${it.stage_to}`;
    if (it.action === "syncro_comment_pulled") return it.body || "(Syncro comment)";
    return it.action.replace(/_/g, " ");
  }

  const badge = state.unread_count > 0;
  return (
    <div className="relative" data-testid="admin-updates-bell-wrap">
      <button
        data-testid="admin-updates-bell"
        onClick={() => setOpen(o => !o)}
        className="relative inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-800 hover:bg-slate-950 text-sm text-slate-300 transition-colors"
        title={badge ? `${state.unread_count} unread update${state.unread_count === 1 ? "" : "s"} from Steele Cloud` : "Steele Cloud updates"}
      >
        <Bell className={`w-4 h-4 ${badge ? "text-amber-300" : "text-slate-400"}`} />
        <span className="hidden md:inline text-xs">Updates</span>
        {badge && (
          <span data-testid="admin-updates-count" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center">
            {state.unread_count > 99 ? "99+" : state.unread_count}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="admin-updates-dropdown" className="absolute right-0 top-full mt-2 w-96 max-h-[70vh] overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 shadow-2xl shadow-black/40 z-50">
          <div className="sticky top-0 bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center justify-between gap-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Steele Cloud activity</div>
              <div className="text-[11px] text-slate-500">{badge ? `${state.unread_count} unread` : "All caught up"}</div>
            </div>
            <div className="flex items-center gap-3">
              {badge && (
                <button data-testid="admin-updates-mark-read" onClick={markAllRead} disabled={busy} className="text-xs text-cyan-300 hover:text-cyan-200 disabled:opacity-50">Mark all read</button>
              )}
              {(state.items || []).length > 0 && state.items.some(x => x.unread) && (
                <button data-testid="admin-updates-clear-all" onClick={markAllRead} disabled={busy} className="text-xs text-slate-400 hover:text-red-300 disabled:opacity-50">Clear all</button>
              )}
            </div>
          </div>
          {(state.items || []).filter(x => x.unread).length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-500">
              {(state.items || []).length === 0 ? "No updates from Steele Cloud yet." : "All caught up — no unread updates."}
            </div>
          ) : (
            <ul className="divide-y divide-slate-800">
              {(state.items || []).filter(x => x.unread).map((it) => {
                const goto = async () => {
                  setState((s) => ({ ...s, unread_count: 0, items: (s.items || []).map(x => ({ ...x, unread: false })) }));
                  try { await api.post("/partner/updates/mark-read"); } catch (_) { /* best-effort */ }
                  setOpen(false);
                  // Deep-link into the specific card's drawer if we know which
                  // one the notification refers to. MigrationBoard reads ?open=
                  // on mount and pops the drawer for us.
                  navigate(it.client_id
                    ? `/portal/onboarding?open=${it.client_id}`
                    : `/portal/onboarding`);
                };
                return (
                  <li key={it.id}>
                    <button onClick={goto} data-testid={`admin-update-item-${it.id}`} className={`w-full text-left px-4 py-3 hover:bg-slate-950 transition-colors ${it.unread ? "border-l-2 border-amber-400" : ""}`}>
                      <div className="flex items-start gap-2">
                        {it.action === "note_added" ? <MessageSquare className="w-3.5 h-3.5 shrink-0 mt-0.5 text-cyan-400" /> : <ActivityIcon className="w-3.5 h-3.5 shrink-0 mt-0.5 text-cyan-400" />}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-slate-100 font-medium truncate">{it.business_name}</div>
                          <div className="text-xs text-slate-400 line-clamp-2 whitespace-pre-wrap">{summarise(it)}</div>
                          <div className="mt-1 text-[10px] text-slate-500 flex items-center gap-2">
                            <span className="text-cyan-300">{it.actor_email}</span>
                            <span>·</span>
                            <span>{new Date(it.created_at).toLocaleString(undefined, {month:"short", day:"2-digit", hour:"2-digit", minute:"2-digit"})}</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function PartnerShell() {
  const { state, logout } = useAuth();
  const navigate = useNavigate();
  const demoMode = !!state?.tenant?.demo_mode;

  async function doLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const navCls = ({ isActive }) =>
    "inline-flex items-center gap-1.5 text-sm font-medium transition-colors " +
    (isActive ? "text-cyan-400" : "text-slate-400 hover:text-slate-100");

  return (
    <div className="min-h-screen bg-slate-950">
      {demoMode && (
        <div
          data-testid="demo-mode-banner"
          className="flex items-center justify-center gap-2 bg-amber-500/15 border-b border-amber-500/40 text-amber-200 text-xs font-semibold uppercase tracking-widest py-2.5 px-4"
        >
          <FlaskConical className="w-3.5 h-3.5" />
          Demo mode — statements shown here are not final. No real payments have been issued.
        </div>
      )}
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl xl:max-w-[100rem] 2xl:max-w-[130rem] mx-auto px-6 lg:px-12 py-5 flex items-center justify-between gap-4">
          <Link to="/portal" data-testid="portal-brand" className="flex items-center shrink-0">
            <img src="/brand/steele-cloud-header.png" alt="Steele Cloud — Partner Portal" className="h-11 w-auto" />
            <span className="ml-4 pl-4 border-l border-slate-800 text-[10px] uppercase tracking-[0.18em] text-cyan-400 font-semibold hidden sm:inline">Partner Portal</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            {state?.user?.role === "partner" && (
              <NavLink to="/portal" end className={navCls} data-testid="portal-nav-earnings">
                <LayoutDashboard className="w-4 h-4" /> Earnings
              </NavLink>
            )}
            <NavLink to="/portal/onboarding" className={navCls} data-testid="portal-nav-onboarding">
              <GitBranch className="w-4 h-4" /> Onboarding
            </NavLink>
            <NavLink to="/portal/tickets" className={navCls} data-testid="portal-nav-tickets">
              <ClipboardSignature className="w-4 h-4" /> Tickets
            </NavLink>
            <NavLink to="/portal/customers" className={navCls} data-testid="portal-nav-customers">
              <UserSquare2 className="w-4 h-4" /> Customers
            </NavLink>
            <NavLink to="/portal/price-list" className={navCls} data-testid="portal-nav-price-list">
              <Tag className="w-4 h-4" /> Price list
            </NavLink>
            <NavLink to="/portal/team" className={navCls} data-testid="portal-nav-team">
              <Users className="w-4 h-4" /> Team
            </NavLink>
            <NavLink to="/portal/feedback" className={navCls} data-testid="portal-nav-feedback">
              <Lightbulb className="w-4 h-4" /> Ideas &amp; issues
            </NavLink>
          </nav>
          <div className="hidden lg:block w-72">
            <HeaderSearch testid="partner-header-search" />
          </div>
          <div className="flex items-center gap-5">
            <AdminUpdatesBell />
            <div className="hidden sm:block text-right">
              <div className="text-sm font-medium">{state?.user?.name || state?.user?.email}</div>
              <div className="text-xs text-slate-500">{state?.user?.email}</div>
            </div>
            <button
              data-testid="portal-logout"
              onClick={doLogout}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-800 hover:bg-slate-950 text-sm font-medium transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl xl:max-w-[100rem] 2xl:max-w-[130rem] mx-auto px-6 lg:px-12 py-12">
        <Outlet />
      </main>
      <footer className="max-w-6xl mx-auto px-6">
        <VersionFooter />
      </footer>
    </div>
  );
}
